
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { Store } from '../store.jsx';
import { clearOrder } from '../actions';
import InactivityCountdown from '../components/InactivityCountdown.jsx';

const INACTIVITY_TIMEOUT = 120000; // 120 seconds in milliseconds
const WARNING_THRESHOLD = 10000; // Show warning 10 seconds before timeout

export const useInactivityTimer = () => {
  const history = useHistory();
  const { dispatch } = useContext(Store);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [timerActive, setTimerActive] = useState(true);
  
  const resetToHome = useCallback(() => {
    clearOrder(dispatch);
    history.push('/');
    setTimerActive(false);
  }, [dispatch, history]);

  const resetTimer = useCallback(() => {
    setSecondsRemaining(0);
    setTimerActive(true);
  }, []);

  useEffect(() => {
    if (!timerActive) return;

    let inactivityTimer;
    let countdownInterval;
    
    const startInactivityTimer = () => {
      // Clear existing timers if any
      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (countdownInterval) clearInterval(countdownInterval);
      
      // Reset seconds remaining
      setSecondsRemaining(0);
      
      // Set timeout for inactivity
      inactivityTimer = setTimeout(() => {
        resetToHome();
      }, INACTIVITY_TIMEOUT);
      
      // Set interval for countdown when approaching timeout
      const checkTimeRemaining = () => {
        const elapsedTime = Date.now() - lastActivity;
        if (elapsedTime >= INACTIVITY_TIMEOUT - WARNING_THRESHOLD) {
          const remaining = Math.ceil((INACTIVITY_TIMEOUT - elapsedTime) / 1000);
          setSecondsRemaining(Math.max(0, remaining));
        } else {
          setSecondsRemaining(0);
        }
      };
      
      countdownInterval = setInterval(checkTimeRemaining, 1000);
    };
    
    let lastActivity = Date.now();
    
    const handleUserActivity = () => {
      lastActivity = Date.now();
      startInactivityTimer();
    };
    
    // Set up event listeners for user activity
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    // Initialize timer
    startInactivityTimer();
    
    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleUserActivity);
    });
    
    // Clean up
    return () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (countdownInterval) clearInterval(countdownInterval);
      
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
  }, [resetToHome, timerActive]);

  return {
    countdown: (
      <InactivityCountdown 
        secondsRemaining={secondsRemaining} 
        onUserActivity={resetTimer}
        onTimeout={resetToHome}
      />
    )
  };
};

export default useInactivityTimer;
